import './styles/global.css';
import './app';
import { Router } from './router';
import { EventBus, AccountManager } from './utils';
import { initLogCollector, markRouteStart, markRouteEnd } from './log-collector';

(window as any).__DAYAN_BUILD__ = new Date().toISOString();

const pageImports: Record<string, () => Promise<unknown>> = {
  'page-dashboard': () => import('./pages/dashboard'),
  'page-project-regular': () => import('./pages/project-regular'),
  'page-project-new': () => import('./pages/project-new'),
  'page-storyboard': () => import('./pages/storyboard'),
  'page-model-selector': () => import('./pages/model_selector'),
  'page-tasks': () => import('./pages/tasks'),
  'page-library': () => import('./pages/library'),
  'page-projects': () => import('./pages/projects'),
  'page-voice-library': () => import('./pages/voice-library'),
  'page-dubbing': () => import('./pages/dubbing'),
  'page-create-project': () => import('./pages/create-project'),
  'page-project-commentary-sb': () => import('./pages/project-commentary-sb'),
  'page-login': () => import('./pages/login'),
  'page-admin': () => import('./pages/admin'),
  'page-billing': () => import('./pages/billing'),
  'page-account': () => import('./pages/account'),
};

const loadedPages = new Set<string>();

async function ensurePageLoaded(tag: string) {
  if (loadedPages.has(tag)) return;
  const loader = pageImports[tag];
  if (loader) {
    await loader();
    loadedPages.add(tag);
  }
}

Router.register('/');
Router.register('/dashboard');
Router.register('/login');
Router.register('/create');
Router.register('/project/regular');
Router.register('/project/regular/:id');
Router.register('/project/commentary-storyboard');
Router.register('/project/commentary-storyboard/:id');
Router.register('/project/new');
Router.register('/project/commentary');
Router.register('/projects');
Router.register('/storyboard');
Router.register('/storyboard/:id');
Router.register('/model-selector');
Router.register('/tasks');
Router.register('/library/characters');
Router.register('/library/sora-characters');
Router.register('/library/scenes');
Router.register('/library/props');
Router.register('/library/prompts');
Router.register('/library/voices');
Router.register('/dubbing/select');
Router.register('/dubbing/:id');
Router.register('/account');
Router.register('/billing');
Router.register('/admin');
Router.register('/admin/:tab');

const appEl = document.getElementById('app');
if (appEl) {
  const path = Router.getCurrentPath();

  if (path === '/login') {
    appEl.className = '';
    appEl.innerHTML = '';
    ensurePageLoaded('page-login').then(() => {
      const el = document.createElement('page-login');
      appEl.appendChild(el);
    });
  } else {
    const boot = async () => {
      initLogCollector();
      try {
        await AccountManager.ensureAccount();
      } catch {
        Router.navigate('/login', true);
        window.location.reload();
        return;
      }

      appEl.className = '';
      appEl.innerHTML = '';

      const app = document.createElement('manga-app');
      const container = document.createElement('div');
      container.id = 'page-container';
      container.style.cssText = 'width:100%;height:100%;';
      app.appendChild(container);
      appEl.appendChild(app);

      const renderPage = async () => {
        markRouteStart();
        const p = Router.getCurrentPath();

        if (p === '/login') {
          window.location.href = '/login';
          return;
        }

        let tag = 'page-dashboard';
        let found = true;

        if (p.startsWith('/admin')) {
          if (!AccountManager.isAdmin) {
            Router.navigate('/dashboard', true);
            return;
          }
          tag = 'page-admin';
        } else if (p.startsWith('/create')) tag = 'page-create-project';
        else if (p.startsWith('/project/regular')) tag = 'page-project-regular';
        else if (p.startsWith('/project/commentary-storyboard')) tag = 'page-project-commentary-sb';
        else if (p.startsWith('/project/new')) tag = 'page-project-new';
        else if (p.startsWith('/project/commentary')) tag = 'page-project-new';
        else if (p.startsWith('/projects')) tag = 'page-projects';
        else if (p.startsWith('/storyboard')) tag = 'page-storyboard';
        else if (p.startsWith('/model-selector')) tag = 'page-model-selector';
        else if (p.startsWith('/account')) tag = 'page-account';
        else if (p.startsWith('/billing')) tag = 'page-billing';
        else if (p.startsWith('/tasks')) tag = 'page-tasks';
        else if (p.startsWith('/library/voices')) tag = 'page-voice-library';
        else if (p.startsWith('/dubbing/')) tag = 'page-dubbing';
        else if (p.startsWith('/library/')) tag = 'page-library';
        else if (p === '/' || p === '/dashboard') tag = 'page-dashboard';
        else found = false;

        if (!found) {
          container.innerHTML = `
            <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;color:var(--text-secondary);">
              <span style="font-size:64px;opacity:0.3;">404</span>
              <p style="margin:16px 0;">页面不存在</p>
              <a href="/dashboard" style="color:var(--primary);text-decoration:none;">返回工作台</a>
            </div>`;
          return;
        }

        const params = Router.getParams();
        const _ct = ['page-project-regular', 'page-project-commentary-sb', 'page-project-new'];
        const _isCreate = _ct.includes(tag) && !params.id;
        const _backToSelect = tag === 'page-create-project';
        for (const child of Array.from(container.children)) {
          const childTag = child.tagName.toLowerCase();
          if (!_backToSelect && _ct.includes(childTag) && !child.hasAttribute('project-id')) {
            (child as HTMLElement).style.display = 'none';
          } else {
            child.remove();
          }
        }
        if (_isCreate) {
          const cached = container.querySelector(tag + ':not([project-id])') as HTMLElement;
          if (cached) {
            cached.style.display = '';
            markRouteEnd(p);
            EventBus.emit('page:change', { path: p, params, tag });
            return;
          }
        }

        await ensurePageLoaded(tag);

        const el = document.createElement(tag);
        el.className = 'page-transition';
        if (params.id) el.setAttribute('project-id', params.id);
        container.appendChild(el);
        markRouteEnd(p);

        EventBus.emit('page:change', { path: p, params, tag });
      };

      Router.onChange(renderPage);
      Router.init();
      renderPage();
    };

    boot();
  }
}
